import { useEffect, useCallback } from 'react';
import type { EntityDetail } from '@web-config-app/core';
import {
  getRelationshipSchemaItemType,
  isRelationshipEntityReference,
} from '@web-config-app/schema-utils';
import type { EntitySelectControlSchema } from '../../types/controls';
import { useEntityDetailsProps } from '../use-entity-details-props/use-entity-details-props';
import { useRelationship } from '../use-relationship/use-relationship';
import { useRelationshipsData } from '../use-relationships-data/use-relationships-data';
import type { UseEntityFormHandleChange } from '../use-entity-form-handle-change/use-entity-form-handle-change';

interface UseEntityRelationshipSchemaProps {
  schema: EntitySelectControlSchema;
  handleEntityFormChange?: UseEntityFormHandleChange;
}

export const useEntityRelationshipSchema = ({
  schema,
  handleEntityFormChange,
}: UseEntityRelationshipSchemaProps) => {
  if (!isRelationshipEntityReference(schema['x-entity-reference'])) {
    throw new Error('Relationship entity reference schema expected');
  }

  const { relationship, ...entityReferenceAnnotation } =
    schema['x-entity-reference'];

  const {
    entityRootData,
    rootSchema,
    setEntityData,
    includedEntities,
    setIncludedEntities,
    addIncludedEntity,
    isUsingConfigAppBackend,
  } = useEntityDetailsProps();

  const { type: entityType } = getRelationshipSchemaItemType(
    relationship,
    rootSchema,
  );

  /**
   * It looks like `useRelationshipsData` should be moved inside of `useRelationship`.
   * which shouldn't be too bad - just need to also include rootData and onRelationshipChange
   */

  const { getRelationship, setRelationships } = useRelationshipsData({
    schema: rootSchema,
    rootData: entityRootData,
    onRelationshipChange: (data) => {
      if (data) setEntityData(data, 'relationships');
    },
  });

  const { setRelationship, removeRelationship, ...relationshipProps } =
    useRelationship({
      relationship,
      schema: rootSchema,
      getRelationship,
      setRelationships,
      isUsingConfigAppBackend,
    });

  const reorderRelationshipItems = useCallback(
    (fromIndex: number, toIndex: number) => {
      const currentValue = getRelationship(relationship);
      if (!Array.isArray(currentValue)) return;

      if (fromIndex === toIndex) return;

      const updated = [...currentValue];
      const [moved] = updated.splice(fromIndex, 1);
      if (!moved) return;

      const targetIndex = Math.max(0, Math.min(updated.length, toIndex));
      updated.splice(targetIndex, 0, moved);

      setRelationships(relationship, updated);
    },
    [getRelationship, setRelationships, relationship],
  );

  const handleRelationshipChange = (
    relationshipEntity: EntityDetail,
    previousValue?: EntityDetail,
    index?: number,
  ) => {
    setRelationship(relationshipEntity, previousValue, index);
    addIncludedEntity?.(relationshipEntity);
    handleEntityFormChange?.(relationshipEntity.id);
  };

  const handleRemoveRelationship = (previousValue: EntityDetail) => {
    removeRelationship(previousValue);
    handleEntityFormChange?.(undefined);
  };

  useEffect(() => {
    /**
     * When the relationship value changes, remove any entities from the includedEntities
     * array that corresponds to a removed relationship.
     */
    if (
      relationshipProps.value &&
      Array.isArray(includedEntities) &&
      setIncludedEntities
    ) {
      const filteredIncludedEntities = includedEntities.filter(
        (includedEntity: EntityDetail) => {
          // retain any included entities that are not of the same type.
          if (
            isUsingConfigAppBackend
              ? includedEntity.attributes.entityMetadata.domainEntityType !==
                entityType
              : includedEntity.type !== entityType
          )
            return true;
          // search the array for matching entity ID and return true
          // if it's already in the array
          if (Array.isArray(relationshipProps.value)) {
            return Boolean(
              relationshipProps.value.find((r) => r.id === includedEntity.id),
            );
          }
          // if the relationship is a single object, check if the ID
          // is the same
          return includedEntity.id === relationshipProps.value.id;
        },
      );

      // having filtered the value of includedEntities, if there is a different
      // number of entities in the filtered array we need to update the state
      if (filteredIncludedEntities.length !== includedEntities.length) {
        setIncludedEntities(filteredIncludedEntities);
      }
    }
  }, [
    relationshipProps.value,
    entityType,
    includedEntities,
    setIncludedEntities,
    isUsingConfigAppBackend,
  ]);

  return {
    ...entityReferenceAnnotation,
    ...relationshipProps,
    handleRelationshipChange,
    handleRemoveRelationship,
    reorderRelationshipItems,
  };
};
